import styled from "@emotion/styled";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useMemo } from "react";

import { Button, FONT_WEIGHT_SEMI_BOLD, toast } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { NodeSelectionCriteria } from "../../library";
import { useGetById } from "../../state/contextualSelectors";
import type { DesignerNode } from "../../types";
import { EmphasizedMessage } from "../message";

/*
  Node picking is used to pick nodes from the canvas (or layers panel) by clicking on them.

  It differs from node selection in that the selected node remains selected (and the property panel continues
  to show its properties) while other nodes are picked to be used in some way related to the selected node.

  A "shield" is active while node picking is in progress (presently a blurred effect), which prevents interactions
  with other aspects of the UI, and helps to keep focus on the relevant areas. A toast message is also displayed to
  give some information about the picking activity.

  A maximum number of nodes to be picked can be specified, as well as the behavior when the maximum is reached. This
  is useful when picking options for single or multiple selection questions, for example.

  Activation swaps from node selection to node picking, and deactivation returns to node selection.
*/

type CustomSelection = (node: DesignerNode) => boolean;

export type ToastOptions = {
  duration?: number;
  message: string;
  cancelable?: boolean;
};

export type ShieldOptions = {
  excludedAreas: string[];
};

export type NodePickingOptions = {
  prepicked?: number[];
  criteria?: NodeSelectionCriteria & { customSelection?: CustomSelection };
  max?: number;
  onMaxReached?: "stop" | "discard" | "deactivate";
};

type NodePickedCallback = ((nodeId: number) => void) | null;
type NodesPickedCallback = ((nodeIds: number[]) => void) | null;

const pickedNodeIdsAtom = atom<number[] | undefined>();
const nodePickingActiveAtom = atom((get) => get(pickedNodeIdsAtom) !== undefined);

const shieldOptionsAtom = atom<ShieldOptions | undefined>(undefined);
const nodePickingOptionsAtom = atom<Omit<NodePickingOptions, "prepicked"> | undefined>(undefined);
const nodePickingCanceledAtom = atom(false);

const nodePickedCallbackAtom = atom<NodePickedCallback>(null);
const nodesPickedCallbackAtom = atom<NodesPickedCallback>(null);

export const useNodePickingActive = () => useAtomValue(nodePickingActiveAtom);

export const useIsNodePicked = (nodeId: number) => !!useAtomValue(pickedNodeIdsAtom)?.includes(nodeId);

export const useAreaExcludedFromShield = (area?: string) => {
  const shieldOptions = useAtomValue(shieldOptionsAtom);

  return useMemo(() => !!(area && shieldOptions?.excludedAreas.includes(area)), [area, shieldOptions?.excludedAreas]);
};

export const useDeactivateNodePicking = () =>
  useAtomCallback(
    useCallback((get, set) => {
      const canceled = get(nodePickingCanceledAtom);

      if (!canceled) {
        const pickedNodeIds = get(pickedNodeIdsAtom) ?? [];

        const nodesPickedCallback = get(nodesPickedCallbackAtom);
        nodesPickedCallback?.(pickedNodeIds);
      }

      set(pickedNodeIdsAtom, undefined);
      set(nodePickingOptionsAtom, undefined);
      set(nodePickedCallbackAtom, null);
      set(nodesPickedCallbackAtom, null);

      toast.dismiss();
    }, []),
  );

export const useActivateNodePicking = ({
  criteria,
  prepicked = [],
  max,
  onMaxReached = "stop",
}: NodePickingOptions) => {
  const formatMessage = useFormatMessage(formatStrings);

  const deactivateNodePicking = useDeactivateNodePicking();

  const setNodePickingOptions = useSetAtom(nodePickingOptionsAtom);

  setNodePickingOptions({ criteria, max, onMaxReached });

  return useAtomCallback(
    useCallback(
      (
        get,
        set,
        onNodePicked: NodePickedCallback = null,
        onNodesPicked: NodesPickedCallback = null,
        options: { shield?: ShieldOptions; toast?: ToastOptions } = {},
      ) => {
        set(nodePickedCallbackAtom, () => onNodePicked);
        set(nodesPickedCallbackAtom, () => onNodesPicked);

        set(shieldOptionsAtom, options.shield);
        set(pickedNodeIdsAtom, prepicked);
        set(nodePickingCanceledAtom, false);

        if (options.toast) {
          const { duration = Infinity, message, cancelable = false } = options.toast;

          toast(
            () => (
              <ToastContent>
                <EmphasizedMessage content={message} components={{ emphasized: EmphasizedText }} />

                {cancelable && (
                  <StyledButton
                    onClick={() => {
                      set(nodePickingCanceledAtom, true);
                      deactivateNodePicking();
                    }}
                  >
                    {formatMessage("cancel")}
                  </StyledButton>
                )}
              </ToastContent>
            ),
            { duration, style: { maxWidth: "unset" } },
          );
        }
      },
      [deactivateNodePicking, formatMessage, prepicked],
    ),
  );
};

export const usePickNodeId = () => {
  const getById = useGetById();

  return useAtomCallback(
    useCallback(
      (get, set, id?: number) => {
        const nodePickedCallback = get(nodePickedCallbackAtom);
        const nodesPickedCallback = get(nodesPickedCallbackAtom);

        if (!(id && (nodePickedCallback || nodesPickedCallback))) return;

        const options = get(nodePickingOptionsAtom) ?? {};

        // only managing "typeId" and "custom selection" criteria for the moment
        if (options.criteria) {
          const pickedNode = getById(id);

          if ("typeId" in options.criteria && pickedNode.typeId !== options.criteria.typeId) return;

          if (options.criteria.customSelection && !options.criteria.customSelection(pickedNode)) return;
        }

        const pickedIds = [...(get(pickedNodeIdsAtom) ?? [])];

        if (options.max && pickedIds.length === options.max && !pickedIds.includes(id)) {
          if (options.onMaxReached === "stop") return;

          // simply discarding the first picked id
          if (options.onMaxReached === "discard") {
            pickedIds.shift();
          }
        }

        const updatedIds = pickedIds.includes(id) ? pickedIds.filter((pId) => pId !== id) : [...pickedIds, id];

        if (options.max && updatedIds.length === options.max && options.onMaxReached === "deactivate") {
          nodesPickedCallback?.(updatedIds);
          set(pickedNodeIdsAtom, undefined);
          set(nodePickingOptionsAtom, undefined);
          set(nodePickedCallbackAtom, null);
          set(nodesPickedCallbackAtom, null);
        } else {
          set(pickedNodeIdsAtom, updatedIds);
          nodePickedCallback?.(id);
        }
      },
      [getById],
    ),
  );
};

const ToastContent = styled.div`
  user-select: none;
`;

const StyledButton = styled(Button)`
  margin-inline-start: 8px;
`;

const EmphasizedText = styled.span`
  color: ${({ theme }) => theme.primary};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const formatStrings = {
  cancel: {
    en: "Cancel",
  },
} satisfies FormatMessages;
